import { ResourceHistoryFieldChange } from '@dispatch/shared/types';
import { formatFieldPath } from './fieldLabels';
import { MemberNameResolver } from './membershipValues';

/**
 * Grouping of audit field rows by the collection item that owns them, shared by
 * the grid summary and the detail modal so both name an item the same way and
 * exactly once.
 *
 * It lives here rather than in `HistoryDetailModal` because `historyFormat`
 * consumes it too, and the modal already imports `historyFormat` — putting it in
 * either of those would make the import cycle.
 */

/**
 * Leading `<Collection>.[<id>]` of a field path — the collection item a row
 * belongs to. Anchored to the START (unlike the membership match, which anchors
 * to the end) because this groups by OWNER, not by leaf.
 */
const OWNING_ITEM_PATH = /^([A-Za-z]+)\.\[([^\]]+)\]/;

export type FieldChangeGroup = {
  key: string;
  /** The collection and member this group belongs to; absent for ungrouped rows. */
  collection?: string;
  memberId?: string;
  /** Resolved heading, or `undefined` for ungrouped rows and unresolvable items. */
  heading?: string;
  changes: ResourceHistoryFieldChange[];
};

/**
 * Groups field rows by the collection item that owns them.
 *
 * Deleting a location emits ~23 sibling rows — Name, Street, City, State, Zip,
 * each weekday, and so on — which as a flat table reads as 23 unrelated changes
 * rather than "this location was removed, and here is what it was". One heading
 * per item, using the resolved name, makes that legible.
 *
 * Order is preserved: a group is created when its item is first seen, so rows stay
 * in the order the audit report emitted them.
 */
export function groupFieldChanges(
  changes: readonly ResourceHistoryFieldChange[],
  resolveName: MemberNameResolver
): FieldChangeGroup[] {
  const groups: FieldChangeGroup[] = [];
  const groupsByItem = new Map<string, FieldChangeGroup>();

  changes.forEach((change, index) => {
    const match = OWNING_ITEM_PATH.exec(change.fieldPath);

    // Metadata's bracket token is a human-readable key, not an item reference, so its
    // rows are treated as ungrouped and keep the key in their own label.
    const item = match && match[1] !== 'Metadata' ? match : null;

    if (!item) {
      // Ungrouped rows are deliberately NOT coalesced. They carry no heading, so
      // merging them buys nothing visually, and merging a later one into the group
      // already pushed for an earlier one would move it up the table — a CreatedAt
      // emitted after a collection change would render beside an earlier UpdatedAt,
      // ahead of the group between them, silently breaking emission order.
      groups.push({ key: `ungrouped-${index}`, changes: [change] });
      return;
    }

    const [, collection, memberId] = item;
    const key = `${collection}.${memberId}`;
    const existing = groupsByItem.get(key);

    if (existing) {
      existing.changes.push(change);
      return;
    }

    const group: FieldChangeGroup = {
      key,
      collection,
      memberId,
      // A heading only when the item resolves. An unresolvable item gets none rather
      // than being headed by a raw GUID.
      heading: resolveHeading(collection, memberId, resolveName),
      changes: [change],
    };

    groupsByItem.set(key, group);
    groups.push(group);
  });

  return groups;
}

function resolveHeading(
  collection: string,
  memberId: string,
  resolveName: MemberNameResolver
): string | undefined {
  const memberName = resolveName(collection, memberId);

  // `formatFieldPath` singularizes and prettifies the collection segment; reuse it
  // so the heading matches the labels ("Location", "Selected Attribute Definition").
  const collectionLabel = formatFieldPath(`${collection}.[${memberId}]`);

  return memberName ? `${collectionLabel} (${memberName})` : undefined;
}

/**
 * The label resolver for rows inside a group.
 *
 * A headed group already names its own item, so repeating that identity in every row
 * label would read "Location (Home Office) › Street" under a "Location (Home Office)"
 * heading. But only THAT identity may be suppressed: a nested path carries a second,
 * deeper one (which attribute value changed), and dropping it too made two values under
 * one definition render as identical "Attribute Value › Value" rows. So this masks the
 * group's own member and delegates everything else.
 */
export function labelResolverFor(
  group: FieldChangeGroup,
  resolveName: MemberNameResolver
): MemberNameResolver {
  if (!group.heading) {
    return resolveName;
  }

  return (collection, memberId) =>
    collection === group.collection && memberId === group.memberId
      ? undefined
      : resolveName(collection, memberId);
}

/**
 * The part of a field path BELOW the item its group is headed by, formatted as a
 * label. Used where the heading is rendered next to its rows rather than above
 * them (the one-line grid summary), so "Locations.[g].Street" reads as just
 * "Street" under a "Location (Home Office)" heading instead of repeating it.
 *
 * Returns `''` for the membership row itself (`Locations.[g]`), which has no
 * field below the item — its own value cell already reads "Home Office (deleted)".
 *
 * A nested path keeps its deeper identity: the remainder is run back through
 * `formatFieldPath` WITH the resolver, so
 * `SelectedAttributeDefinitions.[a].SelectedAttributeValuesVarchars.[b].Value`
 * yields "Attribute Value (Shingle) › Value".
 */
export function formatPathWithinItem(
  group: FieldChangeGroup,
  fieldPath: string,
  resolveName: MemberNameResolver
): string {
  const prefix = `${group.collection}.[${group.memberId}]`;
  if (!fieldPath.startsWith(prefix)) {
    return formatFieldPath(fieldPath, labelResolverFor(group, resolveName));
  }

  return formatFieldPath(
    fieldPath.slice(prefix.length).replace(/^\./, ''),
    resolveName
  );
}
